import { AlertTriangle, WifiOff } from "lucide-react";
import type { ReactNode } from "react";
import { ApiError } from "~/lib/api";
import { errorMessageKey } from "~/lib/errors";
import { useT } from "~/lib/i18n";
import { cn } from "~/lib/cn";
import { Button } from "./button";

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn("animate-pulse rounded-[6px] bg-sunken", className)} aria-hidden />;
}

export function EmptyState({
  icon,
  title,
  body,
  action,
  className,
}: {
  icon?: ReactNode;
  title: ReactNode;
  body?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col items-center justify-center gap-3 px-6 py-16 text-center", className)}>
      {icon ? (
        <div className="grid size-11 place-items-center rounded-full bg-sunken text-fg-3 [&_svg]:size-5">{icon}</div>
      ) : null}
      <div className="max-w-sm">
        <p className="font-display text-base font-medium text-fg">{title}</p>
        {body ? <p className="mt-1 text-sm text-fg-2">{body}</p> : null}
      </div>
      {action}
    </div>
  );
}

export function ErrorState({ error, onRetry, className }: { error: unknown; onRetry?: () => void; className?: string }) {
  const t = useT();
  const offline = error instanceof ApiError && error.isNetwork;
  return (
    <EmptyState
      className={className}
      icon={offline ? <WifiOff /> : <AlertTriangle />}
      title={t(errorMessageKey(error))}
      action={
        onRetry ? (
          <Button variant="secondary" size="sm" onClick={onRetry}>
            {t("common.actions.retry")}
          </Button>
        ) : null
      }
    />
  );
}
